import { CSSInterpolation } from "@emotion/serialize";
import styled, { CSSObject } from "@emotion/styled";

import { withPadding } from "@brexhq/next/src/components/layout/Spacers/Spacers";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import { isDarkTheme } from "@brexhq/shared/types/ModuleTheme";
import { AlignmentOptions } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowsModule";

import type { CardProps, LinkIconAxisType } from "./Card";

export const ContentContainer = styled.div<{
  maxWidth?: React.CSSProperties["maxWidth"];
  margin: React.CSSProperties["margin"];
}>(({ maxWidth, margin }) => ({
  maxWidth,
  margin,
  position: "relative",
  width: maxWidth ? "auto" : "100%",
}));

export const CardWrapper = styled.div<{
  cardTheme: CardProps["cardTheme"];
  parentTheme: CardProps["parentTheme"];
  borderRadius: React.CSSProperties["borderRadius"];
  minHeight?: CardProps["minHeight"];
  cardPadding?: CardProps["cardPadding"];
  hasLink: boolean;
  buttonHoverStyles?: CSSInterpolation;
  useStandardBorderStyle?: boolean;
  useDarkThemeHoverStyle?: boolean;
  hasContent?: boolean;
  mobileCardPadding?: CardProps["mobileCardPadding"];
  onMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => void;
}>(
  ({
    cardTheme,
    parentTheme,
    borderRadius,
    minHeight,
    cardPadding = "none",
    theme,
    hasLink,
    buttonHoverStyles = {},
    useStandardBorderStyle = false,
    useDarkThemeHoverStyle = false,
    hasContent = false,
    mobileCardPadding,
  }) => {
    const {
      card: { borders, textPrimary, background },
    } = getModuleThemeColors(cardTheme);
    const isDarkCardOnDarkTheme =
      parentTheme === cardTheme && isDarkTheme(cardTheme);

    const cardBorderColor = isDarkCardOnDarkTheme
      ? theme.colors.content.tertiary
      : borders;
    const cardHoverBorderColor = textPrimary;

    const darkThemeHoverStyles: CSSObject = useDarkThemeHoverStyle
      ? {
          position: "relative",
          transition: "border 0.3s ease-in-out",
          "&::after": {
            position: "absolute",
            content: '""',
            top: "var(--mouse-hover-y, 0)",
            left: "var(--mouse-hover-x, 0)",
            width: "200%",
            height: "200%",
            background:
              "radial-gradient(calc(var(--hover-orb) * 1px) circle at top, rgba(255,134,66,0.28), transparent), radial-gradient(calc(var(--inner-hover-orb) * 1px) circle, rgba(237,218,153,0.25), transparent), radial-gradient(calc(var(--hover-orb) * 1px) circle at bottom, rgba(0,255,251,0.18), transparent)",
            transform: "translate(-50%, -50%)",
            transition: "opacity 0.3s ease-in-out",
            opacity: 0,
          },
          "&:hover": {
            borderColor: cardBorderColor,
          },
          "&:hover::after": {
            opacity: 0.5,
          },
        }
      : {};

    const linkHoverStyle = hasLink
      ? {
          transition: "box-shadow .5s ease-in-out, border .5s ease-in-out",
          "&:hover": {
            borderColor: useStandardBorderStyle
              ? cardHoverBorderColor
              : undefined,
            boxShadow: !useStandardBorderStyle
              ? // TODO (kristin Nov 7 2022) - replace with theme shadow
                "0px 4px 16px rgba(0, 0, 0, 0.12)"
              : undefined,
            "div[data-name='Icon']": {
              color: theme.colors.content.inverse.primary,
              backgroundColor: theme.colors.accent.primary.background.default,
            },
            "div[data-name='Button']": buttonHoverStyles,
          },
          ...darkThemeHoverStyles,
        }
      : {};

    const borderStyle = useStandardBorderStyle
      ? { border: `1px solid ${cardBorderColor}` }
      : useDarkThemeHoverStyle
      ? { border: `1px solid ${background}` }
      : {};

    return withPadding(
      {
        LRG: cardPadding,
        MED: cardPadding,
        SML: mobileCardPadding ?? cardPadding,
      },
      {
        borderRadius,
        height: "100%",
        minHeight: minHeight
          ? `${minHeight}px`
          : !hasContent
          ? "300px"
          : "initial",
        backgroundColor: background,
        overflow: "hidden",
        ...linkHoverStyle,
        ...borderStyle,
      },
    );
  },
);

export const MediaWrapper = styled.div<{
  cardPadding?: CardProps["cardPadding"];
  mediaFillLocation?: CardProps["mediaFillLocation"] | "background";
  mediaFillSize?: CardProps["mediaFillSize"];
  mobileCardPadding?: CardProps["mobileCardPadding"];
  verticalAlignment?: CardProps["verticalAlignment"];
  desktopMediaHeight?: CardProps["desktopMediaHeight"];
}>(
  ({
    cardPadding = "none",
    theme,
    mediaFillLocation,
    mediaFillSize,
    mobileCardPadding,
    verticalAlignment,
    desktopMediaHeight,
  }) => {
    const isMediaLeft = mediaFillLocation === "left";
    const isMediaRight = mediaFillLocation === "right";
    const isMediaTop = mediaFillLocation === "top";
    const isMediaBottom = mediaFillLocation === "bottom";
    const isMediaBG = mediaFillLocation === "background";

    const marginResetsByFillLocation = (breakpoint: BreakPointVals) => {
      if (breakpoint === "SML") {
        const snapsToTop =
          (isMediaLeft || isMediaRight) &&
          (verticalAlignment === "bottom" || verticalAlignment === "center");
        const snapsToBottom =
          (isMediaLeft || isMediaRight) &&
          (verticalAlignment === "top" || verticalAlignment === "center");
        return {
          marginTop: isMediaBottom || snapsToBottom ? "0px" : undefined,
          marginBottom: isMediaTop || snapsToTop ? "0px" : undefined,
        };
      }
      return {
        marginRight: isMediaLeft ? "0px" : undefined,
        marginLeft: isMediaRight ? "0px" : undefined,
        marginTop: isMediaBottom ? "0px" : undefined,
        marginBottom: isMediaTop ? "0px" : undefined,
      };
    };

    const paddingValue =
      mediaFillSize === "cover" ? theme.spacing.LRG[cardPadding] : 0;
    const mobilePaddingValue =
      mediaFillSize === "cover" && mobileCardPadding
        ? theme.spacing.SML[mobileCardPadding]
        : paddingValue;

    const getWidthStyling = (breakpoint: BreakPointVals) => {
      //  on small width, always 100% width
      if (breakpoint === "SML") {
        return `calc(100% + ${mobilePaddingValue * 2}px)`;
      }
      // when left or right media, want it to be 50% width
      if (isMediaLeft || isMediaRight) {
        return `calc(50% + ${paddingValue}px)`;
      }
      return `calc(100% + ${paddingValue * 2}px)`;
    };

    return {
      order: 0,
      position: isMediaBG ? "absolute" : "relative",
      margin: `-${paddingValue}px`,
      height: desktopMediaHeight
        ? `${desktopMediaHeight}px`
        : `calc(100% + ${paddingValue * 2}px)`,
      width: getWidthStyling("LRG"),
      ...marginResetsByFillLocation("LRG"),
      overflow: "hidden",
      [theme.breakpoints.MED]: {
        margin: `-${paddingValue}px`,
        height: `calc(100% + ${paddingValue * 2}px)`,
        ...marginResetsByFillLocation("MED"),
      },
      [theme.breakpoints.SML]: {
        width: getWidthStyling("SML"),
        margin: `-${mobilePaddingValue}px`,
        height: `calc(100% + ${mobilePaddingValue * 2}px)`,
        ...marginResetsByFillLocation("SML"),
      },
    };
  },
);

const getObjectPosition = {
  left: "right",
  right: "left",
  top: "bottom",
  bottom: "top",
};

export const CardMedia = styled(BynderAsset)<{
  scaleMediaUp?: boolean;
  inlineImageLocation?: CardProps["mediaFillLocation"];
  mediaFillSize?: CardProps["mediaFillSize"];
  borderRadius?: React.CSSProperties["borderRadius"];
}>(
  ({
    scaleMediaUp,
    inlineImageLocation,
    theme,
    mediaFillSize = "cover",
    borderRadius,
  }) => {
    const scaleStyle: CSSObject = scaleMediaUp
      ? {
          height: "150%",
          top: "50%",
          position: "relative",
          transform: "translate(0,-50%)",
        }
      : {};

    return {
      objectFit: mediaFillSize !== "contain" ? mediaFillSize : undefined,
      objectPosition:
        mediaFillSize !== "contain" && inlineImageLocation
          ? getObjectPosition[inlineImageLocation]
          : undefined,
      height: mediaFillSize !== "contain" ? "100%" : undefined,
      width: mediaFillSize !== "contain" ? "100%" : undefined,
      borderRadius,
      ...scaleStyle,
      [theme.breakpoints.SML]: {
        objectPosition: "center",
      },
    };
  },
);

export const FullHeightAndWidthLink = styled(Link)({
  height: "100%",
  width: "100%",
});

export const IconContainer = styled.div<{
  linkIconAxis?: LinkIconAxisType;
  linkIconStyle?: CardProps["linkIconStyle"];
  isLinkIconOverlappingImage?: boolean;
  isLinkIconOverlappingImageMobile?: boolean;
  verticalAlignment: CardProps["verticalAlignment"];
}>(
  ({
    theme,
    linkIconAxis,
    linkIconStyle = "dark",
    isLinkIconOverlappingImage = "false",
    isLinkIconOverlappingImageMobile = "false",
    verticalAlignment,
  }) => {
    const getLocationCSS = (): CSSObject => {
      if (!linkIconAxis) return {};
      const location = {};
      location[linkIconAxis.horizontal] = 0;
      location[linkIconAxis.vertical] = 0;
      return location;
    };

    const darkColor = theme.colors.content.primary;
    const lightColor = theme.colors.content.inverse.primary;

    const verticalMargins =
      linkIconAxis?.vertical !== "contentEnd"
        ? {
            marginBottom:
              linkIconAxis?.vertical === "top" &&
              verticalAlignment !== linkIconAxis?.vertical &&
              verticalAlignment !== "center"
                ? "auto"
                : undefined,
            marginTop:
              linkIconAxis?.vertical === "bottom" &&
              verticalAlignment !== linkIconAxis?.vertical &&
              verticalAlignment !== "center"
                ? "auto"
                : undefined,
          }
        : {};

    return withPadding(
      { LRG: "xxsml", MED: "xxsml", SML: "xxsml" },
      {
        display: "inline-flex",
        marginLeft: linkIconAxis?.horizontal === "right" ? "auto" : undefined,
        color: linkIconStyle === "light" ? darkColor : lightColor,
        position: isLinkIconOverlappingImage ? "absolute" : "relative",
        backgroundColor: linkIconStyle === "light" ? lightColor : darkColor,
        borderRadius: theme.radius.half,
        zIndex: theme.zIndex.POPOVER,
        ...getLocationCSS(),
        transition: "color .2s ease-in-out, background-color .2s ease-in-out",
        ...verticalMargins,
        [theme.breakpoints.SML]: {
          position: isLinkIconOverlappingImageMobile ? "absolute" : "relative",
        },
      },
    );
    //   hover colors are applied through card parent
  },
);
export const TextRowsWrapper = styled.div<{
  verticalAlignment: CardProps["verticalAlignment"];
  horizontalAlignment: AlignmentOptions;
  hasInlineImage: boolean;
  linkIconAxis?: LinkIconAxisType;
  isIconStyle?: boolean;
}>(
  ({
    theme,
    verticalAlignment,
    horizontalAlignment,
    hasInlineImage,
    linkIconAxis,
    isIconStyle,
  }) => {
    const mapHorizontalAlignmentToFlex = {
      left: "flex-start",
      right: "flex-end",
      center: "center",
    };
    // Horizontal Alignment on text content only applies when no inline image is provided.
    // Otherwise, Alignment is handled relative to the position of the inline image
    const baseStyles = !hasInlineImage
      ? {
          display: "flex",
          justifyContent: mapHorizontalAlignmentToFlex[horizontalAlignment],
        }
      : {};

    return isIconStyle && linkIconAxis?.vertical !== "contentEnd"
      ? {
          ...baseStyles,
          width: "100%",
          marginBottom:
            verticalAlignment === "top" || verticalAlignment === "center"
              ? "auto"
              : undefined,
          marginTop:
            verticalAlignment === "bottom" || verticalAlignment === "center"
              ? "auto"
              : undefined,
          [theme.breakpoints.SML]: {
            marginTop: 0,
            marginBottom: 0,
          },
        }
      : {
          width: "100%",
          ...baseStyles,
        };
  },
);
export const ContentWrapper = styled.div<{
  mediaFillLocation?: CardProps["mediaFillLocation"];
  contentMaxWidth?: CardProps["contentMaxWidth"];
  verticalAlignment: CardProps["verticalAlignment"];
  hasInlineImage?: boolean;
  horizontalAlignment?: React.CSSProperties["alignItems"];
}>(
  ({
    theme,
    mediaFillLocation,
    contentMaxWidth,
    verticalAlignment,
    hasInlineImage,
    horizontalAlignment,
  }) => {
    const isMediaLeftOrRight =
      mediaFillLocation === "left" || mediaFillLocation === "right";
    const contentOrder =
      mediaFillLocation === "top" || mediaFillLocation == "left" ? 1 : 0;
    const mobileContentOrder =
      verticalAlignment === "top" || verticalAlignment === "center" ? 0 : 1;

    const mapVerticalAlignmentToFlex = {
      top: "flex-start",
      bottom: "flex-end",
      center: "center",
    };
    return {
      display: "flex",
      flexDirection: "column",
      height: hasInlineImage && !isMediaLeftOrRight ? "auto" : "100%",
      justifyContent: mapVerticalAlignmentToFlex[verticalAlignment],
      alignItems: horizontalAlignment ?? "flex-start",
      order: contentOrder,
      zIndex: 1,
      maxWidth: `${contentMaxWidth}px`,
      width: hasInlineImage && isMediaLeftOrRight ? "50%" : "100%",
      [theme.breakpoints.SML]: {
        width: "100%",
        height: "auto",
        order: isMediaLeftOrRight ? mobileContentOrder : contentOrder,
      },
    };
  },
);
